/**
 * The short type a business card prints. Stored categories and subcategories
 * are long editorial descriptions ("Puerto Rican & Spanish Deli"), so the card
 * shows the plain phrase a local would say out loud instead — "Spanish
 * restaurant", "Bakery". First match wins; an unlisted place falls back to its
 * plain directory type.
 */
const RULES = [
  // Eat & drink
  [/bakery|bakehouse|pastr|bread|cake|dessert|sweets/, "Bakery"],
  [/spanish|paella/, "Spanish restaurant"],
  [/sushi|japanese/, "Sushi"],
  [/pizzer|pizza|calzone/, "Pizza"],
  [/taco|mexican/, "Mexican"],
  [/bbq|barbecue/, "BBQ"],
  [/tapas/, "Tapas bar"],
  [/sandwich|brunch/, "Sandwiches"],
  [/vegetarian|vegan/, "Vegetarian"],
  [/fine dining/, "Fine dining"],
  [/coffee|caf|cafeteria|breakfast|bistro/, "Café"],
  [/criollo|creole|puerto rican|home-style/, "Puerto Rican restaurant"],
  // Nightlife
  [/sports bar/, "Sports bar"],
  [/nightclub/, "Nightclub"],
  [/live music|live salsa/, "Live music"],
  // Getting around
  [/transfer|shuttle|taxi|chauffeur|transport|car service|limousine|private driver/, "Transport"],
  // Shops & makers
  [/bookstore|bookseller/, "Bookstore"],
  [/botánica|\bbotanica\b|herbal/, "Botánica"],
  [/mercado|market/, "Market"],
  [/concept store|apparel|boutique|shop/, "Shop"],
  [/lounge|cocktail|beer|nightlife|street food|kiosk|\bbar\b|\bbars\b|\bpub\b/, "Bar"],
  // Culture & landmarks
  [/mural/, "Mural"],
  [/sculpture/, "Sculpture"],
  [/museum/, "Museum"],
  [/fort|citadel/, "Fort"],
  [/church|chapel|cathedral/, "Church"],
  [/distiller|rum/, "Distillery"],
  [/promenade|waterfront/, "Waterfront"],
  [/science|educational/, "Science park"],
  [/garden|park/, "Park"],
  [/reserve|nature/, "Nature reserve"],
  [/arena/, "Arena"],
  [/entertainment|complex/, "Entertainment"],
  [/historic|colonial|capitol|residence|government/, "Historic site"],
];

const BY_TYPE = {
  Restaurant: "Restaurant",
  Shop: "Shop",
  Nightlife: "Bar",
  Culture: "Landmark",
  Service: "Service",
  Experience: "Experience",
};

const firstMatch = (text) => RULES.find(([pattern]) => pattern.test(text))?.[1] || "";

export function shortType(listing) {
  if (!listing) return "";
  // LGBTQ+ nightlife should be visibly identified on cards without forcing
  // every queer venue into one separate rail. Its subcategory still places it
  // alongside comparable bars or nightclubs in the broader nightlife browse.
  if (String(listing.category || "").trim().toLowerCase() === "lgbtq+" && String(listing.type || "").toLowerCase() === "nightlife") {
    const detail = `${listing.subcategory || ""} ${listing.name || ""}`.toLowerCase();
    return /night ?club|nightclub|\bclub\b|discoteca/.test(detail) ? "LGBTQ+ Nightclub" : "LGBTQ+ Bar";
  }
  // The subcategory is the most specific, the name the last resort — so a
  // place whose fields are just "Eat & Drink" still lands on something useful.
  const fields = [listing.subcategory, listing.category, listing.name];
  for (const field of fields) {
    const hit = firstMatch((field || "").toLowerCase());
    if (hit) return hit;
  }
  return BY_TYPE[listing.type] || "Place";
}